import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem } from "@mui/material";
import { Grid } from "@mui/material";
import Close from "@mui/icons-material/Close";
import Save from "@mui/icons-material/Save";
import DialogDateField from "./DialogDateField";
import { normalizeDialogDate } from "./dialogDate";


const normalizeDateValue = (value) => {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
};

const PersonDialog = ({ open, onClose, onSave, person }) => {
  const [Typ, setTyp] = useState(person?.Typ || "PERSON");
  const [Organisationsname, setOrganisationsname] = useState(person?.Organisationsname || "");
  const [Kontaktfunktion, setKontaktfunktion] = useState(person?.Kontaktfunktion || "");
  const organization = Typ === "ORGANIZATION";
  const [Name, setName] = useState(person ? person.Name : "");
  const [Vorname, setVorname] = useState(person ? person.Vorname : "");
  const [Geburtsdatum, setGeburtsdatum] = useState(normalizeDateValue(person?.Geburtsdatum));
  const [Strassenr, setStrassenr] = useState(person ? person.Strassenr : "");
  const [Plz, setPlz] = useState(person ? person.Plz : "");
  const [Ort, setOrt] = useState(person ? person.Ort : "");
  const [Telefon1, setTelefon1] = useState(person ? person.Telefon1 : "");
  const [Telefon2, setTelefon2] = useState(person ? person.Telefon2 : "");
  const [Mail, setMail] = useState(person ? person.Mail : "");
  const [Iban, setIban] = useState(person ? person.Iban : "");
  const [Bemerkungen, setBemerkungen] = useState(person ? person.Bemerkungen || "" : "");
  
  useEffect(() => {
    if (!open) return;
    setTyp(person?.Typ || "PERSON");
    setOrganisationsname(person?.Organisationsname || "");
    setKontaktfunktion(person?.Kontaktfunktion || "");
    if (person) {
      setName(person.Name || "");
      setVorname(person.Vorname || "");
      setGeburtsdatum(normalizeDateValue(person.Geburtsdatum));
      setStrassenr(person.Strassenr || "");
      setPlz(person.Plz || "");
      setOrt(person.Ort || "");
      setTelefon1(person.Telefon1 || "");
      setTelefon2(person.Telefon2 || "");
      setMail(person.Mail || "");
      setIban(person.Iban || "");
      setBemerkungen(person.Bemerkungen || "");
    } else {
      setName("");
      setVorname("");
      setGeburtsdatum("");
      setStrassenr("");
      setPlz("");
      setOrt("");
      setTelefon1("");
      setTelefon2("");
      setMail("");
      setIban("");
      setBemerkungen("");
    }
  }, [open, person]); // Bei jedem Öffnen neu initialisieren, auch bei wiederholter Neuanlage.

  const handleSave = () => {
    if (organization && !Organisationsname.trim()) return;
    const payload = {
      Typ,
      Organisationsname: organization ? Organisationsname.trim() : null,
      Kontaktfunktion: organization ? Kontaktfunktion : null,
      Name,
      Vorname,
      Geburtsdatum: normalizeDialogDate(Geburtsdatum),
      Strassenr,
      Plz,
      Ort,
      Telefon1,
      Telefon2,
      Mail,
      Iban,
      Bemerkungen,
    };
    onSave(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{person ? "Person bearbeiten" : "Neue Person hinzufügen"}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField select fullWidth size="small" label="Typ" value={Typ} onChange={e => setTyp(e.target.value)}>
              <MenuItem value="PERSON">Person</MenuItem>
              <MenuItem value="ORGANIZATION">Organisation</MenuItem>
            </TextField>
          </Grid>
          {organization && <>
            <Grid item xs={12}><TextField fullWidth size="small" required label="Organisationsname"
              value={Organisationsname} onChange={e => setOrganisationsname(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 255 } }} /></Grid>
            <Grid item xs={12}><TextField fullWidth size="small" label="Kontaktfunktion"
              value={Kontaktfunktion} onChange={e => setKontaktfunktion(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 100 } }} /></Grid>
          </>}
          {/* Name & Vorname (gleich groß nebeneinander) */}
          <Grid item xs={6}>
            <TextField 
              width="240px" 
              size="small"
              label={organization ? "Kontakt Nachname" : "Name"}
              variant="outlined" 
              margin="dense" 
              value={Name} 
              onChange={(e) => setName(e.target.value)} 
            />
          </Grid>
          <Grid item xs={6}>
            <TextField 
              width="240px" 
              size="small"
              label={organization ? "Kontakt Vorname" : "Vorname"}
              variant="outlined" 
              margin="dense" 
              value={Vorname} 
              onChange={(e) => setVorname(e.target.value)} />
          </Grid>

          {/* Geburtstag */}
          {!organization && <Grid item xs={6}>
            <DialogDateField
              type="date"
              width="240px" 
              size="small"
              label="Geburtstag"
              value={Geburtsdatum}
              onChange={(e) => setGeburtsdatum(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>}

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Straße / Hausnummer"
              variant="outlined"
              margin="dense"
              value={Strassenr}
              onChange={(e) => setStrassenr(e.target.value)}
            />
          </Grid>

          {/* PLZ (halb so groß wie Ort) & Ort nebeneinander */}
          <Grid item xs={4}>
            <TextField 
              width="240px" 
              size="small"
              label="PLZ" 
              variant="outlined" 
              margin="dense" 
              value={Plz} 
              onChange={(e) => setPlz(e.target.value)} 
            />
          </Grid>
          <Grid item xs={8}>
            <TextField 
              width="240px" 
              size="small"
              label="Ort" 
              variant="outlined" 
              margin="dense" 
              value={Ort} 
              onChange={(e) => setOrt(e.target.value)} 
            />
          </Grid>

          {/* Telefonnummern */}
          <Grid item xs={6}>
            <TextField 
              width="240px" 
              size="small"
              label="Telefon 1" 
              variant="outlined" 
              margin="dense" 
              value={Telefon1} 
              onChange={(e) => setTelefon1(e.target.value)} 
            />
          </Grid>
          <Grid item xs={6}>
            <TextField 
              width="240px" 
              size="small"
              label="Telefon 2" 
              variant="outlined" 
              margin="dense" 
              value={Telefon2} 
              onChange={(e) => setTelefon2(e.target.value)} 
            />
          </Grid>

          {/* E-Mail & IBAN */}
          <Grid item xs={12}>
            <TextField 
              width="240px" 
              size="small"
              label="E-Mail" 
              variant="outlined" 
              margin="dense" 
              value={Mail} 
              onChange={(e) => setMail(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12}>
            <TextField 
              width="240px" 
              size="small"
              label="IBAN" 
              variant="outlined" 
              margin="dense" 
              value={Iban} 
              onChange={(e) => setIban(e.target.value)} 
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Bemerkungen"
              variant="outlined"
              margin="dense"
              multiline
              minRows={3}
              value={Bemerkungen}
              onChange={(e) => setBemerkungen(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>      <DialogActions>
        <Button onClick={onClose} color="secondary" startIcon={<Close />}>Abbrechen</Button>
        <Button disabled={organization && !Organisationsname.trim()} onClick={handleSave} color="primary" variant="contained" startIcon={<Save />}>Speichern</Button>
      </DialogActions>
    </Dialog>
  );
};


export default PersonDialog;
